// Training Stress Score (TSS) Calculation Utilities

/**
 * Calculate TSS based on power data
 * TSS = (NP^3 / FTP^2) * (duration / 3600) * 100
 * 
 * @param {number} normalizedPower - Normalized power in watts
 * @param {number} duration - Duration in seconds
 * @param {number} ftp - Functional Threshold Power in watts
 * @returns {number} TSS value
 */
export function calculatePowerTSS(normalizedPower, duration, ftp) {
  if (!ftp || ftp <= 0) return 0;
  if (duration <= 0) return 0;
  
  const intensityFactor = Math.pow(normalizedPower / ftp, 3);
  const durationHours = duration / 3600;
  const tss = intensityFactor * durationHours * 100;
  
  return Math.round(tss * 10) / 10; // Round to 1 decimal
}

/**
 * Calculate TSS based on heart rate data
 * TSS = (avgHR / thresholdHR)^2 * (duration / 3600) * 100
 * 
 * @param {number} avgHR - Average heart rate in bpm
 * @param {number} duration - Duration in seconds
 * @param {number} thresholdHR - Lactate threshold HR in bpm
 * @returns {number} TSS value
 */
export function calculateHRTSS(avgHR, duration, thresholdHR) {
  if (!thresholdHR || thresholdHR <= 0) return 0;
  if (duration <= 0) return 0;
  
  const intensityFactor = Math.pow(avgHR / thresholdHR, 2);
  const durationHours = duration / 3600;
  const tss = intensityFactor * durationHours * 100;
  
  return Math.round(tss * 10) / 10;
}

/**
 * Calculate TSS based on pace (running)
 * TSS = (thresholdPace / avgPace)^2 * (duration / 3600) * 100
 * 
 * @param {number} avgPace - Average pace in seconds per km
 * @param {number} duration - Duration in seconds
 * @param {number} thresholdPace - Threshold pace in seconds per km
 * @returns {number} TSS value
 */
export function calculatePaceTSS(avgPace, duration, thresholdPace) {
  if (!thresholdPace || thresholdPace <= 0) return 0;
  if (avgPace <= 0 || duration <= 0) return 0;
  
  const intensityFactor = Math.pow(thresholdPace / avgPace, 2);
  const durationHours = duration / 3600;
  const tss = intensityFactor * durationHours * 100;
  
  return Math.round(tss * 10) / 10;
}

/**
 * Calculate TSS based on time and estimated intensity (fallback for beginners)
 * TSS = estimatedIntensity^2 * (duration / 3600) * 100
 * 
 * @param {number} duration - Duration in seconds
 * @param {number} estimatedIntensity - Perceived effort 0.0-1.0
 * @returns {number} TSS value
 */
export function calculateTimeTSS(duration, estimatedIntensity = 0.6) {
  if (duration <= 0) return 0;
  
  // Clamp intensity between 0 and 1
  const intensity = Math.max(0, Math.min(1, estimatedIntensity));
  
  const intensityFactor = Math.pow(intensity, 2);
  const durationHours = duration / 3600;
  const tss = intensityFactor * durationHours * 100;
  
  return Math.round(tss * 10) / 10;
}

/**
 * Calculate TSS from workout data using best available method
 * 
 * @param {Object} workoutData - Workout data object
 * @param {Object} zones - User's training zones
 * @returns {Object} TSS calculation result
 */
export function calculateTSS(workoutData, zones = {}) {
  const { duration, normalizedPower, avgHR, avgPace } = workoutData;
  const { powerZones, hrZones, paceZones } = zones;
  
  // Priority: Power > HR > Pace > Time
  if (normalizedPower && powerZones?.ftp) {
    return {
      tss: calculatePowerTSS(normalizedPower, duration, powerZones.ftp),
      method: 'power',
      details: {
        normalizedPower,
        ftp: powerZones.ftp,
        duration
      }
    };
  }
  
  if (avgHR && hrZones?.lactateThresholdHR) {
    return {
      tss: calculateHRTSS(avgHR, duration, hrZones.lactateThresholdHR),
      method: 'hr',
      details: {
        avgHR,
        thresholdHR: hrZones.lactateThresholdHR,
        duration
      }
    };
  }
  
  if (avgPace && paceZones?.thresholdPace) {
    return {
      tss: calculatePaceTSS(avgPace, duration, paceZones.thresholdPace),
      method: 'pace',
      details: {
        avgPace,
        thresholdPace: paceZones.thresholdPace,
        duration
      }
    };
  }
  
  // Fallback to time-based with default moderate intensity
  const estimatedIntensity = workoutData.estimatedIntensity || 0.6;
  return {
    tss: calculateTimeTSS(duration, estimatedIntensity),
    method: 'time',
    details: {
      duration,
      estimatedIntensity
    }
  };
}

/**
 * Estimate TSS for a planned workout based on duration and target zone
 * 
 * @param {number} duration - Planned duration in seconds
 * @param {number} targetZone - Target training zone (1-5)
 * @returns {number} Estimated TSS
 */
export function estimateWorkoutTSS(duration, targetZone = 2) {
  // Zone intensity multipliers (approximate)
  const zoneIntensities = {
    1: 0.5, // Recovery
    2: 0.65, // Aerobic
    3: 0.8, // Tempo
    4: 0.95, // Threshold
    5: 1.1 // VO2max
  };
  
  const intensity = zoneIntensities[targetZone] || 0.65;
  return calculateTimeTSS(duration, intensity);
}

/**
 * Calculate CTL (Chronic Training Load) - 42-day fitness metric
 * CTL = previousCTL * (1 - 1/42) + dailyTSS * (1/42)
 * 
 * @param {number} previousCTL - Previous CTL value
 * @param {number} dailyTSS - TSS for the day
 * @param {number} days - Number of days for decay (default 42)
 * @returns {number} New CTL value
 */
export function calculateCTL(previousCTL, dailyTSS, days = 42) {
  const decay = 1 - (1 / days);
  const newCTL = previousCTL * decay + dailyTSS * (1 / days);
  return Math.round(newCTL * 10) / 10;
}

/**
 * Calculate ATL (Acute Training Load) - 7-day fatigue metric
 * ATL = previousATL * (1 - 1/7) + dailyTSS * (1/7)
 * 
 * @param {number} previousATL - Previous ATL value
 * @param {number} dailyTSS - TSS for the day
 * @param {number} days - Number of days for decay (default 7)
 * @returns {number} New ATL value
 */
export function calculateATL(previousATL, dailyTSS, days = 7) {
  const decay = 1 - (1 / days);
  const newATL = previousATL * decay + dailyTSS * (1 / days);
  return Math.round(newATL * 10) / 10;
}

/**
 * Calculate TSB (Training Stress Balance) - Form indicator
 * TSB = CTL - ATL
 * 
 * @param {number} ctl - Chronic Training Load
 * @param {number} atl - Acute Training Load
 * @returns {Object} TSB value and interpretation
 */
export function calculateTSB(ctl, atl) {
  const tsb = Math.round((ctl - atl) * 10) / 10;
  
  let interpretation = 'neutral';
  if (tsb > 15) {
    interpretation = 'very-fresh';
  } else if (tsb > 5) {
    interpretation = 'fresh';
  } else if (tsb < -15) {
    interpretation = 'overtrained';
  } else if (tsb < -5) {
    interpretation = 'tired';
  }
  
  return {
    tsb,
    ctl,
    atl,
    interpretation
  };
}
